import { useEffect, useRef, useState, useCallback } from 'react'
import format from 'date-fns/format'
import { useQuery } from 'react-query'
import SEO from './../utils/SEOMetaTags'
import { ScheduleList, DateNavigation, Loading } from '@scrippsproduct/scripps-external-component-library'
import { create2WeeksList, getCurrentEasternProgrammingDateStr, chunkArray } from '@scrippsproduct/scripps-external-component-library/dist/utils/date-helpers'
import useGetSchedule from '@scrippsproduct/scripps-external-component-library/dist/hooks/useGetSchedule'

import './../scss/Schedule.scss'

const SCHED_EP = process.env.REACT_APP_SCHEDULE_V2
const NETWORK = process.env.REACT_APP_NETWORK

const GetSchedule = async ({ day }) => {
    return useGetSchedule({ 
        dateStr: format(day, 'yyyy-MM-dd'),
        schedEndpoint: SCHED_EP,
        network: NETWORK,
    })
}

export default function Schedule() {
    const weeks = (window.innerWidth > 600) ? chunkArray(create2WeeksList(), 7) : chunkArray(create2WeeksList(), 7)
    // if it's before 6am, we need to start on the previous day's schedule since our programming day starts at 6am
    const [day, setDay] = useState(new Date(`${getCurrentEasternProgrammingDateStr()}T00:00:00`))
    const { isError, isSuccess, data } = useQuery(
        [`schedule:${format(day, 'yyyy-MM-dd')}`, { resourceName: 'schedule', resourceId: format(day, 'yyyy-MM-dd'),  day: format(day, 'yyyy-MM-dd') }], () => GetSchedule({ 
            day, 
            oa: process.env.REACT_APP_OA,
            ct: process.env.REACT_APP_CT,
            scheduleApiUrl: process.env.REACT_APP_SCHEDULE_V2, 
        })
    )
    const [schedule, setSchedule] = useState(null)
    const [exiting, setExiting] = useState(false)
    const [fetchingList, setFetchingList] = useState(true)
    const currentDate = useRef(day)
    const dateListEl = useRef()
    const dateListOffset = useRef()
    
    const determinePointerPosition = useCallback((date) => {
        let currentDateListEl
        const currentDateBtn = document.querySelector(`.date-navigation__day[aria-controls="${format(date, 'yyyy-MM-dd')}"]`)
        if (currentDateBtn) currentDateListEl = currentDateBtn.parentElement

        if (currentDateListEl) dateListEl.current.style.setProperty('--pointer-position', `${currentDateListEl.offsetLeft + (currentDateListEl.offsetWidth/2)}px`)
    }, [])

    useEffect(() => {
        window.addEventListener('resize', determinePointerPosition)

        return () => {
            window.removeEventListener('resize', determinePointerPosition)
        }
    }, [determinePointerPosition])

    useEffect(() => {
        if (isSuccess) setSchedule(data)
    }, [isSuccess, data])

    useEffect(() => {
        if (schedule !== null) {
            setFetchingList(false)
            setExiting(false)
        }
    }, [schedule])

    useEffect(() => {
        // set the initial pointer position
        let currentDateListEl        
        const currentDateBtn = document.querySelector('.date-navigation__day[aria-selected="true"]')
        if (currentDateBtn) currentDateListEl = currentDateBtn.parentElement
    
        if (currentDateListEl) {
            const newOffset = currentDateListEl.offsetLeft + (currentDateListEl.offsetWidth/2)   
            dateListEl.current = document.querySelector('.date-navigation__list')
            dateListOffset.current = newOffset
            dateListEl.current.style.setProperty('--pointer-position', `${newOffset}px`)
        }
        // setup observer to change the state of the sticky date navigation
        const observationEl = document.querySelector(".header-divider")
        const stickyWrapperEl = document.querySelector("sticky-content-wrapper")
        if (stickyWrapperEl) {
            const observer = new IntersectionObserver( 
                ([e]) => {  
                    if (e.intersectionRatio < 1) {
                        stickyWrapperEl.dataset.pinnedState = 'pinned'
                    } else {
                        stickyWrapperEl.dataset.pinnedState = 'unpinned'
                    }
                },
                { 
                    threshold: [1],
                    rootMargin: '-37px', 
                }
            );

            observer.observe(observationEl);
        }
    }, [])

    const handleDateChanged = date => {
        if ( format(date, 'yyyy-MM-dd') === format(currentDate.current, 'yyyy-MM-dd') ) return

        setExiting(true)
        // wait for fade animation to complete before fetching new schedule
        setTimeout(() => {
            setFetchingList(true)
            setSchedule(null)
            setDay(date)
            currentDate.current = date  
            determinePointerPosition(date)

            const schedListEl = document.querySelector('.schedule-content-constrainer > ul')
            const stickyWrapperEl = document.querySelector("sticky-content-wrapper")
            const mobileNavEl = document.querySelector('.mobile-nav')
            // detrmine if the mobile nav css display attribute is set to block
            if (schedListEl) window.scrollTo({
                behavior: 'auto',
                top: (mobileNavEl && window.getComputedStyle(mobileNavEl).display !== 'none') 
                    ? schedListEl.offsetTop - stickyWrapperEl.offsetHeight - mobileNavEl.offsetHeight - 8 
                    : schedListEl.offsetTop - stickyWrapperEl.offsetHeight - 16,
                left: 0,
            })
        }, 325)
    }

    return (
        <>  
            <sticky-content-wrapper data-pinned-state="unpinned">
                <content-constrainer class="content-constrainer--no-padding content-constrainer--no-border content-constrainer--lead-container schedule-date-nav-constrainer">                  
                    <DateNavigation weeks={weeks} handleDateChanged={handleDateChanged} />         
                </content-constrainer>
            </sticky-content-wrapper>
            <section className='page-section' id="schedule-section">
                <content-constrainer class="content-constrainer--no-border schedule-content-constrainer">
                    {(() => {
                        if (isError) return (
                            <div className="schedule-list__no-schedule">
                                <h2>There was an error loading the schedule for this day.</h2>
                                <p>Please try again later. If this problem persists report it at <a href='https://support.iontelevision.com/support/home'>our support page</a>.</p>
                            </div> 
                        )    
                        if (fetchingList || schedule === null) return <Loading topOffset="40%"/>                    
                        if (schedule !== null) {
                            if (schedule.length === 0 && !fetchingList) {
                                return (
                                    <div className="schedule-list__no-schedule">
                                        <h2>Our programming schedule for today has completed.</h2>
                                        <p>Our programming will restart at 6am.</p>
                                    </div>
                                )
                            } else if (schedule[0]?.queryStatus === 'no-results')  {
                                return (
                                    <div className="schedule-list__no-schedule" style={{
                                        animationDelay: '500ms',
                                    }}>
                                        <h2>There are no shows scheduled for this day.</h2>
                                        <p>Please try again later. If this problem persists report it at <a href='https://support.iontelevision.com/support/home'>our support page</a>.</p>
                                    </div>
                                )
                            } else {
                                return <ScheduleList schedule={schedule} exiting={exiting} />
                            }                                 
                        }  
                        return null 
                    })()}
                </content-constrainer>
            </section>
        </>
    )
}
